import VisaLogo from './images/visa.svg'
import MastercardLogo from './images/mastercard.svg'
import AmexLogo from './images/amex.svg'
import GenericLogo from './images/generic.svg'

export const PAYMENT_TYPE = {
  AMERICAN_EXPRESS: {
    KEY: 'AMERICAN_EXPRESS',
    LOGO: AmexLogo,
  },
  VISA: {
    KEY: 'VISA',
    LOGO: VisaLogo,
  },
  MASTERCARD: {
    KEY: 'MASTERCARD',
    LOGO: MastercardLogo,
  },
  CARD: {
    KEY: 'CARD',
    LOGO: GenericLogo,
  },
  CHECKING: { KEY: 'CHECKING', LOGO: 'cil-bank' },
  SAVINGS: { KEY: 'SAVINGS', LOGO: 'cil-bank' },
}

export enum PAYMENT_METHOD_STATUS {
  'Active' = 'ACTIVE' as any,
  'Pending verification' = 'PENDING_VERIFICATION' as any,
  'Expired' = 'EXPIRED' as any,
}

export const isPrimaryPaymentMethod = (
  paymentMethod: { id: string; default?: boolean },
  primaryPaymentMethodId?: string | null
) =>
  primaryPaymentMethodId
    ? paymentMethod.id === primaryPaymentMethodId
    : Boolean(paymentMethod.default);

function parseExpiryEndDate(expiryDate: string): Date | null {
  const cleaned = expiryDate.replace(/\s/g, "");
  const match = cleaned.match(/^(\d{1,2})\/(\d{2,4})$/);
  if (!match) {
    return null;
  }

  const month = Number.parseInt(match[1], 10);
  let year = Number.parseInt(match[2], 10);
  if (month < 1 || month > 12) {
    return null;
  }

  if (year < 100) {
    year += 2000;
  }

  return new Date(year, month, 0, 23, 59, 59, 999);
}

export const isPaymentMethodExpired = (
  expiryDate?: string | null,
  status?: string | null
): boolean => {
  if (status === "EXPIRED") {
    return true;
  }

  if (!expiryDate) {
    return false;
  }

  const expiryEnd = parseExpiryEndDate(expiryDate);
  return expiryEnd != null && expiryEnd < new Date();
};

export const isEligibleAutopayPaymentMethod = (paymentMethod: {
  type: string;
  status?: string | null;
  expiryDate?: string | null;
}): boolean => {
  // Callers substitute a `{ type: "" }` placeholder for a payment method they could not resolve,
  // and the slice's initial default is an all-empty-string object. Neither is chargeable, and an
  // empty status would otherwise skip the status check below.
  if (!paymentMethod.type) {
    return false;
  }

  if (
    paymentMethod.type === "CHECK" ||
    paymentMethod.type === "DIRECT_DEPOSIT"
  ) {
    return false;
  }

  if (paymentMethod.status && paymentMethod.status !== "ACTIVE") {
    return false;
  }

  return !isPaymentMethodExpired(
    paymentMethod.expiryDate,
    paymentMethod.status
  );
};

export const isEligibleDefaultPaymentMethod = isEligibleAutopayPaymentMethod;

export const getDefaultEligibleAutopayPaymentMethodId = (
  paymentMethods: {
    id: string;
    default?: boolean;
    type: string;
    status?: string | null;
    expiryDate?: string | null;
  }[],
  preferredDefault?: { id: string; type: string; status?: string | null; expiryDate?: string | null } | false | null
): string => {
  const eligiblePaymentMethods = paymentMethods.filter(
    isEligibleAutopayPaymentMethod
  );

  if (
    preferredDefault &&
    isEligibleAutopayPaymentMethod(preferredDefault)
  ) {
    return preferredDefault.id;
  }

  const eligibleDefault = eligiblePaymentMethods.find(
    (paymentMethod) => paymentMethod.default
  );

  return eligibleDefault?.id ?? eligiblePaymentMethods[0]?.id ?? "";
};

export const getAutopayPaymentMethodIneligibilityReason = (paymentMethod: {
  type: string;
  status?: string | null;
  expiryDate?: string | null;
}): string | null => {
  if (isEligibleAutopayPaymentMethod(paymentMethod)) {
    return null;
  }

  if (
    paymentMethod.type === "CHECK" ||
    paymentMethod.type === "DIRECT_DEPOSIT"
  ) {
    return "This payment method type cannot be used for AutoPay.";
  }

  if (isPaymentMethodExpired(paymentMethod.expiryDate, paymentMethod.status)) {
    return "This card has expired and cannot be used for AutoPay.";
  }

  if (paymentMethod.status === "PENDING_VERIFICATION") {
    return "This payment method is pending verification and cannot be used for AutoPay.";
  }

  if (paymentMethod.status && paymentMethod.status !== "ACTIVE") {
    return "This payment method is inactive and cannot be used for AutoPay.";
  }

  return "This payment method cannot be used for AutoPay.";
};

export const formatPaymentMethodLabel = (paymentMethod: {
  providerName: string;
  last4: string;
}): string => `${paymentMethod.providerName} (••••${paymentMethod.last4})`;

export const formatPaymentMethodExpiration = (paymentMethod: {
  type: string;
  expiryDate?: string | null;
}): string => {
  if (paymentMethod.type !== "CREDIT_CARD" || !paymentMethod.expiryDate) {
    return "—";
  }

  return paymentMethod.expiryDate;
};

export const getPaymentMethodStatusBadgeVariant = (paymentMethod: {
  type: string;
  status?: string | null;
  expiryDate?: string | null;
}): "expired" | "pending" | "inactive" | null => {
  if (isPaymentMethodExpired(paymentMethod.expiryDate, paymentMethod.status)) {
    return "expired";
  }

  if (paymentMethod.status === "PENDING_VERIFICATION") {
    return "pending";
  }

  if (paymentMethod.status && paymentMethod.status !== "ACTIVE") {
    return "inactive";
  }

  return null;
};

export const formatPaymentMethodStatusDisplaySuffix = (paymentMethod: {
  type: string;
  status?: string | null;
  expiryDate?: string | null;
}): string => {
  if (paymentMethod.type === "CREDIT_CARD") {
    if (isPaymentMethodExpired(paymentMethod.expiryDate, paymentMethod.status)) {
      return " · Expired";
    }

    return "";
  }

  if (!paymentMethod.status || paymentMethod.status === "ACTIVE") {
    return "";
  }

  switch (paymentMethod.status) {
    case "PENDING_VERIFICATION":
      return " · Pending verification";
    case "EXPIRED":
      return " · Expired";
    default:
      return " · Inactive";
  }
};

export const formatPaymentMethodDisplay = (paymentMethod: {
  providerName: string;
  last4: string;
  type: string;
  expiryDate?: string | null;
  status?: string;
}): string => {
  let display = formatPaymentMethodLabel(paymentMethod);

  if (paymentMethod.type === "CREDIT_CARD" && paymentMethod.expiryDate) {
    display += ` · Exp ${paymentMethod.expiryDate}`;
  }

  display += formatPaymentMethodStatusDisplaySuffix(paymentMethod);

  return display;
};
